import { AxiosError } from "axios";

/**
 * Returns a user-friendly message for API failures (Axios errors, CORS, 5xx, etc.).
 */
/** Message shown when the backend cannot reach the database (e.g. Neo4j). */
const DATABASE_UNAVAILABLE_MESSAGE =
  "The data service is temporarily unavailable. Please try again later.";

function isDatabaseConnectionError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("cannot resolve address") ||
    lower.includes("resolve address") ||
    lower.includes("neo4j") ||
    lower.includes("database") && (lower.includes("connection") || lower.includes("unavailable") || lower.includes("refused"))
  );
}

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data;
    const serverMessage =
      typeof data?.message === "string"
        ? data.message
        : typeof data?.detail === "string"
          ? data.detail
          : null;
    const combinedMessage = [serverMessage, error.message].filter(Boolean).join(" ");

    if (combinedMessage && isDatabaseConnectionError(combinedMessage)) {
      return DATABASE_UNAVAILABLE_MESSAGE;
    }
    if (error.code === "ERR_NETWORK" || error.message?.toLowerCase().includes("network")) {
      return "Network error. Please check your connection and try again.";
    }
    if (error.message?.toLowerCase().includes("cors") || error.message?.toLowerCase().includes("blocked")) {
      return "Request was blocked (CORS). The API may be unavailable or misconfigured.";
    }
    if (status === 500) {
      return serverMessage || "The server encountered an error (500). Please try again later.";
    }
    if (status === 502 || status === 503) {
      return serverMessage || "Service temporarily unavailable. Please try again later.";
    }
    if (status === 404) {
      return serverMessage || "The requested resource was not found (404).";
    }
    if (status && status >= 400) {
      return serverMessage || `Request failed (${status}). Please try again.`;
    }
    return serverMessage || error.message || "An unexpected error occurred.";
  }
  if (error instanceof Error) {
    if (isDatabaseConnectionError(error.message)) {
      return DATABASE_UNAVAILABLE_MESSAGE;
    }
    return error.message;
  }
  return "An unexpected error occurred.";
}
